import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  Divider,
  CircularProgress,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Tooltip,
  Collapse,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import LanguageSelect from '../shared/LanguageSelect';
import axios from 'axios';
import { useSubscriptionStore, useUserStore } from '../../store';
import TemplatePicker from './TemplatePicker';
import ImagePickerButton from '../shared/ImagePickerButton';
import SmsSegmentCounter from '../shared/SmsSegmentCounter';
import { NOTE_IMAGE_MAX_UPLOAD_SIZE, NOTE_IMAGE_ALLOWED_TYPES, NOTE_IMAGE_ACCEPT_STRING } from './imageConstants';
import CompletionOdometerField, { EMPTY_ODOMETER, odometerAllowsSubmit, odometerPayload } from './CompletionOdometerField';
import { needsOdometerPrompt } from './stateTransitions';

function CompletionDialog({ open, handleClose, maintenance, setMaintenance }) {
  const { t } = useTranslation();
  const { subscriptionInfo, fetchSubscriptionInfo, canUseSms } = useSubscriptionStore();
  const odometerRequired = useUserStore((s) => !!s.currentOrganization?.require_odometer_on_completion);

  // Form state
  const [sendEmail, setSendEmail] = useState(false);
  const [sendSms, setSendSms] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [language, setLanguage] = useState(maintenance?.customer?.preferred_language || 'fi');
  const [smsFrame, setSmsFrame] = useState(null);
  const [odometer, setOdometer] = useState(EMPTY_ODOMETER);

  // UI state
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [notifyExpanded, setNotifyExpanded] = useState(false);
  const [result, setResult] = useState(null);

  // Check if SMS is available (not available during trial)
  const smsEnabled = canUseSms();
  const isTrial = subscriptionInfo?.is_trial;

  // Odometer prompt while the work order has no reading of its own (services/odometer.py).
  const showOdometer = needsOdometerPrompt(maintenance, 'completed');

  // Check if customer has contact info
  const hasCustomer = !!maintenance?.customer;
  const hasEmail = !!maintenance?.customer?.email;
  const hasPhone = !!maintenance?.customer?.phone;

  useEffect(() => {
    if (open && maintenance?.id) {
      // Pre-fill contact info
      setEmail(maintenance.customer?.email || '');
      setPhone(maintenance.customer?.phone || '');
      setLanguage(maintenance.customer?.preferred_language || 'fi');
      setResult(null);
      setError(null);
      setMessage('');
      setSelectedImages([]);
      setSendEmail(false);
      setSendSms(false);
      setNotifyExpanded(false);
      setOdometer(EMPTY_ODOMETER);

      // Fetch subscription info if not already loaded
      if (!subscriptionInfo) {
        fetchSubscriptionInfo();
      }
    }
  }, [open, maintenance?.id]);

  // SMS overhead frame for the segment counter: the sent SMS wraps the typed text
  // in org name + tracking URL, so the counter must count the assembled message.
  useEffect(() => {
    if (!open || !maintenance?.id) {
      setSmsFrame(null);
      return;
    }
    axios
      .get(`/api/maintenances/${maintenance.id}/sms-frame/`, {
        params: { language, target_state: 'completed' },
      })
      .then((response) => setSmsFrame(response.data))
      .catch(() => setSmsFrame(null));
  }, [open, maintenance?.id, language]);

  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter((file) => {
      if (!NOTE_IMAGE_ALLOWED_TYPES.includes(file.type)) {
        setError(t('Invalid file type. Allowed: JPEG, PNG, WebP, GIF'));
        return false;
      }
      if (file.size > NOTE_IMAGE_MAX_UPLOAD_SIZE) {
        setError(t('File too large (max {{maxMB}}MB)', { maxMB: NOTE_IMAGE_MAX_UPLOAD_SIZE / 1024 / 1024 }));
        return false;
      }
      return true;
    });

    const newImages = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));

    setSelectedImages((prev) => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);

    try {
      const response = await axios.post(
        `/api/maintenances/${maintenance.id}/generate_customer_note/`,
        { language, for_completion: true }
      );
      setMessage(response.data.generated_text);
      // Auto-expand notification section if we generated a message
      setNotifyExpanded(true);
    } catch (err) {
      setError(err.response?.data?.error || t('Failed to generate note'));
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('send_email', sendEmail);
      formData.append('send_sms', sendSms);
      formData.append('language', language);

      if (sendEmail && email) {
        formData.append('email', email);
      }
      if (sendSms && phone) {
        formData.append('phone', phone);
      }
      if (message.trim()) {
        formData.append('message', message.trim());
      }
      if (showOdometer) {
        Object.entries(odometerPayload(odometer).payload).forEach(([key, val]) => formData.append(key, val));
      }

      selectedImages.forEach((img) => {
        formData.append('images', img.file);
      });

      const response = await axios.post(
        `/api/maintenances/${maintenance.id}/complete_with_notification/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setMaintenance(response.data);

      // Show warnings if any
      if (response.data.image_warnings) {
        console.warn('Image upload warnings:', response.data.image_warnings);
      }

      // If no notifications were sent, close immediately
      // Otherwise show result with success/error status
      if (!sendEmail && !sendSms) {
        handleCancel();
      } else {
        setResult(response.data.notification_results);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('Error completing maintenance'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Cleanup previews
    selectedImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setMessage('');
    setSelectedImages([]);
    setSendEmail(false);
    setSendSms(false);
    setEmail('');
    setPhone('');
    setError(null);
    setResult(null);
    setNotifyExpanded(false);
    setOdometer(EMPTY_ODOMETER);
    handleClose();
  };

  const canSubmit = !loading && (!sendEmail || email) && (!sendSms || phone)
    && (!showOdometer || odometerAllowsSubmit(odometer, odometerRequired));

  // If we have a result (notifications were attempted), show success/error state
  const showSuccess = !!result;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 1 }}>
        <Box sx={{ mb: 2 }}>
          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
        </Box>
        {t('Complete maintenance?')}
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ textAlign: 'center', mb: 3 }}>
          {t('This will mark the maintenance as completed. Make sure all work is finished.')}
        </DialogContentText>

        {/* Success result display */}
        {showSuccess && (
          <Box sx={{ mt: 2 }}>
            {result.email_sent && (
              <Alert severity="success" sx={{ mb: 1, borderRadius: '8px' }}>
                {t('Email sent successfully')}
              </Alert>
            )}
            {result.sms_sent && (
              <Alert severity="success" sx={{ mb: 1, borderRadius: '8px' }}>
                {t('SMS sent successfully')}
              </Alert>
            )}
            {result.email_error && (
              <Alert severity="warning" sx={{ mb: 1, borderRadius: '8px' }}>
                {t('Email failed')}: {result.email_error}
              </Alert>
            )}
            {result.sms_error && (
              <Alert severity="warning" sx={{ mb: 1, borderRadius: '8px' }}>
                {t('SMS failed')}: {result.sms_error}
              </Alert>
            )}
            <Alert severity="success" sx={{ borderRadius: '8px' }}>
              {t('Maintenance completed successfully')}
            </Alert>
          </Box>
        )}

        {/* Form content - hide after success */}
        {!showSuccess && (
          <>
            {showOdometer && (
              <CompletionOdometerField
                maintenance={maintenance}
                required={odometerRequired}
                value={odometer}
                onChange={setOdometer}
              />
            )}

            <Divider sx={{ my: 2 }} />

            {/* Notify Customer Section Header */}
            <Box
              onClick={() => setNotifyExpanded(!notifyExpanded)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                py: 1,
                '&:hover': { bgcolor: 'action.hover' },
                borderRadius: '8px',
                px: 1,
                mx: -1,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>
                  {t('Notify customer (optional)')}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  {t('Customer will receive a ready-for-pickup notification with vehicle details')}
                </Typography>
              </Box>
              {notifyExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>

            <Collapse in={notifyExpanded}>
              <Box sx={{ pt: 2 }}>
                {!hasCustomer && (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
                    {t('No customer assigned to this maintenance')}
                  </Alert>
                )}

                {/* Language selector */}
                <LanguageSelect
                  size="small"
                  sx={{ mb: 2, minWidth: 200, display: 'block' }}
                  value={language}
                  label={t('Message language')}
                  onChange={(e) => setLanguage(e.target.value)}
                />

                {/* Email checkbox + field */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      disabled={!hasCustomer && !email}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 20 }} />
                      <Typography>{t('Send via email')}</Typography>
                    </Box>
                  }
                />
                {sendEmail && (
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('Email address')}
                    sx={{ mb: 2, ml: 4, width: 'calc(100% - 32px)' }}
                  />
                )}

                {/* SMS checkbox + field */}
                <Tooltip
                  title={!smsEnabled && isTrial ? t('SMS is not available during trial') : ''}
                  placement="right"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendSms}
                        onChange={(e) => setSendSms(e.target.checked)}
                        disabled={!smsEnabled || (!hasCustomer && !phone)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SmsIcon sx={{ fontSize: 20, color: smsEnabled ? 'inherit' : 'text.disabled' }} />
                        <Typography sx={{ color: smsEnabled ? 'inherit' : 'text.disabled' }}>
                          {t('Send via SMS')}
                        </Typography>
                        {!smsEnabled && isTrial && (
                          <Tooltip title={t('SMS is not available during trial')}>
                            <InfoIcon sx={{ fontSize: 16, color: 'info.main', ml: 0.5 }} />
                          </Tooltip>
                        )}
                      </Box>
                    }
                  />
                </Tooltip>
                {!smsEnabled && isTrial && (
                  <Alert severity="info" sx={{ mb: 2, ml: 4, width: 'calc(100% - 32px)', borderRadius: '8px' }}>
                    {t('SMS notifications are available with a paid subscription. Upgrade to send SMS messages to customers.')}
                  </Alert>
                )}
                {sendSms && smsEnabled && (
                  <TextField
                    fullWidth
                    size="small"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t('Phone number')}
                    sx={{ mb: 2, ml: 4, width: 'calc(100% - 32px)' }}
                  />
                )}

                {/* Message input (shown if email or SMS enabled OR if user wants to add note) */}
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: '14px', color: 'text.secondary', mb: 0.5 }}>
                    {t('Additional notes (optional)')}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 1 }}>
                    {t('You do not need to write anything - the notification includes a default message. Add notes only if you want to include extra details.')}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t('e.g., Oil change completed, brakes checked...')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      },
                    }}
                  />
                  {sendSms && smsEnabled && smsFrame && (
                    <SmsSegmentCounter
                      prefix={smsFrame.prefix}
                      suffix={smsFrame.suffix}
                      text={message.trim()}
                      defaultMessage={smsFrame.default_message}
                    />
                  )}
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <TemplatePicker
                      maintenance={maintenance}
                      onSelect={(text) => {
                        setMessage(text);
                        setNotifyExpanded(true);
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        generating ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <AutoAwesomeIcon />
                        )
                      }
                      onClick={handleGenerate}
                      disabled={generating}
                      sx={{
                        borderColor: 'secondary.main',
                        color: 'secondary.main',
                        borderRadius: '8px',
                        fontWeight: 500,
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: 'secondary.dark',
                          bgcolor: 'rgba(49, 130, 206, 0.04)',
                        },
                      }}
                    >
                      {generating ? <span>{t('Generating...')}</span> : <span>{t('Generate with AI')}</span>}
                    </Button>
                  </Box>
                </Box>

                {/* Image Upload Section */}
                <Box sx={{ mt: 3 }}>
                  <Typography sx={{ fontSize: '14px', color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ImageIcon sx={{ fontSize: 16 }} />
                    {t('Attach images')}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 1.5 }}>
                    {t('Images will be visible on the tracking page')}
                  </Typography>

                  <ImagePickerButton
                    onChange={handleImageSelect}
                    accept={NOTE_IMAGE_ACCEPT_STRING}
                    multiple
                    buttonLabel={t('Select images')}
                    buttonProps={{
                      variant: 'outlined',
                      startIcon: <AttachFileIcon />,
                      size: 'small',
                      sx: {
                        borderRadius: '8px',
                        fontWeight: 500,
                        textTransform: 'none',
                      },
                    }}
                  />

                  {selectedImages.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: 'text.secondary',
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <ImageIcon sx={{ fontSize: 16 }} />
                        {selectedImages.length}{' '}
                        {selectedImages.length === 1 ? t('image') : t('images')}
                      </Typography>
                      <ImageList
                        sx={{
                          width: '100%',
                          maxHeight: 160,
                          overflow: 'auto',
                        }}
                        cols={4}
                        rowHeight={80}
                        gap={8}
                      >
                        {selectedImages.map((img, index) => (
                          <ImageListItem
                            key={index}
                            sx={{
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <img
                              src={img.preview}
                              alt={img.name}
                              loading="lazy"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                            <ImageListItemBar
                              sx={{
                                background:
                                  'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                                '& .MuiImageListItemBar-titleWrap': {
                                  padding: '4px',
                                },
                              }}
                              position="bottom"
                              actionIcon={
                                <IconButton
                                  size="small"
                                  onClick={() => removeImage(index)}
                                  sx={{
                                    color: 'white',
                                    '&:hover': {
                                      bgcolor: 'rgba(255,255,255,0.2)',
                                    },
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              }
                              actionPosition="right"
                            />
                          </ImageListItem>
                        ))}
                      </ImageList>
                    </Box>
                  )}
                </Box>
              </Box>
            </Collapse>

            {error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, gap: 1, justifyContent: 'center' }}>
        {showSuccess ? (
          <Button
            onClick={handleCancel}
            variant="contained"
            color="success"
            sx={{
              borderRadius: '10px',
              px: 4,
              py: 1,
            }}
          >
            {t('Close')}
          </Button>
        ) : (
          <>
            <Button
              onClick={handleCancel}
              variant="outlined"
              disabled={loading}
              sx={{
                borderRadius: '10px',
                px: 3,
                py: 1,
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'text.primary',
                  bgcolor: 'transparent',
                },
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="success"
              disabled={!canSubmit}
              sx={{
                borderRadius: '10px',
                px: 3,
                py: 1,
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t('Yes, complete')
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default CompletionDialog;
